import { useState, useEffect, useRef, useCallback } from 'react';
import { ImageOff, Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';

const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 3000, 5000];
/** 单次加载看门狗：img 既不 onLoad 也不 onError（请求 pending/挂起）时兜底走重试链。
 *  已移除 loading="lazy"（路由切换后 IntersectionObserver 可能漏触发导致永不加载），
 *  因此可用较短超时——图片应即刻开始加载，8s 内无响应即为网络异常。 */
const LOAD_WATCHDOG_MS = 8000;

interface AssetImageProps {
  src?: string;
  alt?: string;
  className?: string;
}

/**
 * 带 loading 骨架 + 自动递增重试（最多 3 次，1s/3s/5s）的图片。
 * 空 URL 或超过重试上限时显示占位符。
 * 通过 new Image() 编程式预加载触发浏览器缓存，避免路由切换时 DOM img 的 onLoad 漏触发。
 */
export function AssetImage({ src, alt, className }: AssetImageProps): JSX.Element {
  // ── 所有 hooks 必须在顶部声明（React hooks 规则）──
  const [retryCount, setRetryCount] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  // 组件卸载标记
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // src 变更时重置所有状态
  useEffect(() => {
    setRetryCount(0);
    setRetrying(false);
    setFailed(false);
    setLoaded(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, [src]);

  // 当前重试的目标 URL
  const finalSrc = retryCount > 0 ? `${src}${(src ?? '').includes('?') ? '&' : '?'}_r=${retryCount}` : src;

  // 重试调度（通过 ref 避免看门狗闭包过期）
  const scheduleRetry = useCallback(() => {
    if (retryCount < MAX_RETRIES) {
      const delay = RETRY_DELAYS[retryCount] ?? RETRY_DELAYS[RETRY_DELAYS.length - 1];
      setRetrying(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        setRetryCount((c) => c + 1);
        setRetrying(false);
      }, delay);
    } else {
      setFailed(true);
    }
  }, [retryCount]);

  const scheduleRetryRef = useRef(scheduleRetry);
  scheduleRetryRef.current = scheduleRetry;

  // DO-NOT-REVERT: BUG-20260731-008 加载看门狗——请求挂起时永无 onLoad/onError
  useEffect(() => {
    if (!src || loaded || failed || retrying) return;
    const watchdog = setTimeout(() => scheduleRetryRef.current(), LOAD_WATCHDOG_MS);
    return () => clearTimeout(watchdog);
  }, [src, loaded, failed, retrying, retryCount]);

  // 编程式预加载：new Image() 触发浏览器加载，不依赖 DOM img 的 onLoad/onError 时序
  useEffect(() => {
    if (!src || failed) return;
    setLoaded(false);
    const img = new Image();
    let cancelled = false;
    img.onload = () => { if (!cancelled) setLoaded(true); };
    img.onerror = () => { if (!cancelled) scheduleRetryRef.current(); };
    img.src = finalSrc ?? '';
    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [finalSrc, src, failed]);

  // ── 渲染 ──

  if (!src || failed) {
    return (
      <div className={cn('flex items-center justify-center bg-muted/50 text-muted-foreground/40', className)}>
        <ImageOff size={22} />
      </div>
    );
  }

  if (retrying) {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-1 bg-muted/50 text-muted-foreground', className)}>
        <Loader2 size={16} className="animate-spin text-neon-cyan" />
        <span className="text-[9px]">重试中 ({retryCount}/{MAX_RETRIES})</span>
      </div>
    );
  }

  return (
    <div className={cn('relative bg-muted/50', className)}>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 size={16} className="animate-spin text-neon-cyan/60" />
        </div>
      )}
      <img
        src={finalSrc}
        alt={alt ?? ''}
        className={cn('h-full w-full object-cover transition-opacity', loaded ? 'opacity-100' : 'opacity-0')}
      />
    </div>
  );
}
